import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { createSupplierThunk, resetSupplierCreationData } from "redux/Reducers/Concierge/createSupplierSlice";

import { useForm } from "antd/es/form/Form";
import { Col, Form, Row, VertAlert, VertButton, VertInput, VertModal, VertTypography, notification } from "@vertem-core/vertice-ds";

import "Assets/styles/default/components/Concierge/Modals/CreateSupplierModal.less";

const CreateSupplierModal = (props) => {
  const { isOpen, setIsOpen, term, retrieveCreatedSupplier } = props;

  const [ form ] = useForm();

  const dispatch = useDispatch();

  const [showValidationFeedback, setShowValidationFeedback] = useState(false);
  const [hasError, setHasError] = useState(false);

  const {
    supplierCreationData,
    isSupplierCreationLoading,
    supplierCreationHasErrors
  } = useSelector(
    state => state.CreateSupplier
  );

  const masks = {
    telephone: [
      {
        mask: "(00) 00000-0000",
        length: 11,
      },
    ],
    document: [
      {
        mask: "00.000.000/0000-00",
        length: 14,
      },
    ]
  };

  const validateMasks = () => {
    const fieldsWithMask = Object.keys(masks);

    const formValue = form.getFieldsValue();

    fieldsWithMask.forEach(field => {
      if (formValue[field]?.length != masks[field].at(0).length) {
        setHasError(true);
        return;
      }
    });

    setHasError(false);
  }

  const validateSupplierName = (val) => {
    if (!val) {
      setHasError(true);
      return Promise.reject();
    }

    setHasError(false);
    return Promise.resolve();
  }

  const validateMinimalFields = async () => {
    setShowValidationFeedback(true);

    const contactFields = ["telephone", "email", "website", "document", "social"];

    const formValue = form.getFieldsValue();

    const hasAtLeastOneContactInfo = contactFields.some((field) => formValue[field]);

    if (hasAtLeastOneContactInfo) {
      validateMasks();

      return Promise.resolve();
    }

    setHasError(true);

    return Promise.reject();
  }

  const validateNumericFields = (val, size) => {
    if (!val) return;

    const numericValue = val.replace(/\D/g, '');

    if (numericValue.length == size) return Promise.resolve();

    setHasError(true);

    return Promise.reject();
  }

  const baseContactFieldValidation = {
    rules: [
      {
        validator: validateMinimalFields,
      }
    ]
  };

  const validators = {
    supplierName: {
      rules: [
        {
          validator: (_, val) => validateSupplierName(val),
          message: "Campo obrigatório.",
        }
      ]
    },
    telephone: {
      rules: [
        ...baseContactFieldValidation.rules,
        {
          validator: (_, val) => validateNumericFields(val, masks.telephone[0].length),
          message: `Deve ter ${masks.telephone[0].length} caracteres.`,
        }
      ],
    },
    email: {
      rules: [
        ...baseContactFieldValidation.rules
      ]
    },
    website: {
      rules: [
        ...baseContactFieldValidation.rules
      ]
    },
    document: {
      rules: [
        ...baseContactFieldValidation.rules,
        {
          validator: (_, val) => validateNumericFields(val, masks.document[0].length),
          message: `Deve ter ${masks.document[0].length} caracteres.`
        }
      ]
    },
    social: {
      rules: [
        ...baseContactFieldValidation.rules
      ]
    }
  };

  const handleFormSubmission = () => {
    if (!showValidationFeedback) {
      form.validateFields();
      return;
    }

    const formData = form.getFieldsValue();
    const formKeys = Object.keys(formData);

    const supplier = formKeys.reduce((acc, key) => {
      if (formData[key]) acc[key] = formData[key];

      return acc;
    }, {});

    dispatch(createSupplierThunk(supplier));
  }

  useEffect(() => {
    if (!term) return;
  
    form.setFieldValue("supplierName", term);
  }, [term]);

  useEffect(() => {
    if (!supplierCreationData) return;

    retrieveCreatedSupplier(supplierCreationData);

    notification.success({
      message: "Fornecedor criado com sucesso.",
    });

    setIsOpen(false);
    setShowValidationFeedback(false);
    setHasError(false);

    form.resetFields();

    dispatch(resetSupplierCreationData());
  }, [supplierCreationData]);

  useEffect(() => {
    if (!supplierCreationHasErrors) return;

    notification.error({
      message: "Houve um erro ao criar fornecedor.",
    });
  }, [supplierCreationHasErrors]);

  return (
    <VertModal
      width="50%"
      className="concierge-create-supplier-modal"
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      title={"Indicação de fornecedor"}
      destroyOnClose={true}
    >
      <VertTypography.Text className="instructions" type="light">
        Para conseguirmos entrar em contato com o fornecedor <span>preencha o nome da empresa e pelo menos mais um dos campos corretamente:</span>
      </VertTypography.Text>

      {
        showValidationFeedback && (
          hasError ? (
            <VertAlert
              className="status-alert"
              type="error"
              shape="secondary"
              closable={false}
              message="Erro"
              description="Preencha o nome da empresa e pelo menos mais um dos campos corretamente!"
            />
          ) : (
            <VertAlert 
              className="status-alert"
              type="success"
              shape="secondary"
              closable={false}
              message="Obrigado"
              description="Os campos preenchidos são suficientes."
            />
          )
        )
      }

      <Form form={form}>
        <Row className="input-group" gutter={8}>
          <Col span={12}>
            <Form.Item
              name="supplierName"
              rules={validators.supplierName.rules}
              validateTrigger={["onChange"]}
            >
              <VertInput 
                type="text"
                label="Nome"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="telephone"
              rules={validators.telephone.rules}
              validateTrigger={["onChange"]}
            >
              <VertInput 
                type="text"
                label="Celular / WhatsApp"
                mask={masks.telephone}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="email"
              rules={validators.email.rules}
              validateTrigger={["onChange"]}
            >
              <VertInput 
                type="text"
                label="E-mail"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="website"
              rules={validators.website.rules}
              validateTrigger={["onChange"]}
            >
              <VertInput 
                type="text"
                label="Website"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="document"
              rules={validators.document.rules}
              validateTrigger={["onChange"]}
            >
              <VertInput 
                type="text"
                label="CNPJ"
                mask={masks.document}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="social"
              rules={validators.social.rules}
              validateTrigger={["onChange"]}
            >
              <VertInput 
                type="text"
                label="Rede social"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="end">
          <Col className="action-btn-container">
            <VertButton
              className="btn-confirm"
              onClick={() => setIsOpen(false)}
            >
              Cancelar
            </VertButton>

            <VertButton
              className="btn-confirm"
              type="highlight"
              disabled={hasError}
              loading={isSupplierCreationLoading}
              onClick={handleFormSubmission}
            >
              Confirmar
            </VertButton>
          </Col>
        </Row>
      </Form>
    </VertModal>
  );
}

export default CreateSupplierModal;
